/**
 * Heading Component
 *
 * A semantic heading component that automatically applies design system typography
 * based on heading level. Supports gradient text effects.
 *
 * @example
 * ```tsx
 * <Heading level={1}>Page Title</Heading>
 * <Heading level={2} className="text-gradient">Section Title</Heading>
 * <Heading level={3} as="h2">Semantically h2, styled as h3</Heading>
 * ```
 */

import { HTMLAttributes, ReactNode, createElement } from 'react';

export interface HeadingProps extends Omit<HTMLAttributes<HTMLHeadingElement>, 'children'> {
  /** The content to render inside the heading */
  children: ReactNode;
  /** Heading level (1-6) determines visual styling */
  level: 1 | 2 | 3 | 4 | 5 | 6;
  /** Override the semantic HTML element (for accessibility) */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Text color variant */
  color?: 'default' | 'white' | 'primary' | 'muted';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Semantic heading component with design system typography
 */
export const Heading = ({
  children,
  level,
  as,
  color = 'default',
  className = '',
  ...props
}: HeadingProps) => {
  // Use 'as' prop for semantic HTML, or default to level
  const tag = as || `h${level}`;

  // Map level to typography classes (from design system)
  const levelClasses = {
    1: 'text-5xl md:text-6xl lg:text-7xl font-semibold',
    2: 'text-4xl md:text-5xl font-semibold',
    3: 'text-3xl md:text-4xl font-semibold',
    4: 'text-2xl md:text-3xl font-semibold',
    5: 'text-xl md:text-2xl font-semibold',
    6: 'text-lg md:text-xl font-semibold',
  };

  // Map color to text color classes
  const colorClasses = {
    default: 'text-neo-black',
    white: 'text-neo-white',
    primary: 'text-primary',
    muted: 'text-neo-grey',
  };

  return createElement(
    tag,
    {
      className: `${colorClasses[color]} leading-tight ${levelClasses[level]} ${className}`,
      ...props,
    },
    children
  );
};
